/**
 * AETHER STUDIO — ULTRA-PREMIUM HARDWARE THEME ENGINE
 * Complete button interactions, AJAX shopping bag, blueprint tabs, gallery, and variant syncing.
 */

document.addEventListener('DOMContentLoaded', () => {

  // 1. ULTRA-FLUID HIGH-RESPONSIVENESS SMOOTH SCROLLING (LENIS)
  let lenisInstance = null;
  if (typeof Lenis !== 'undefined' && window.innerWidth > 768) {
    try {
      lenisInstance = new Lenis({
        duration: 0.75, // Snappy & responsive, removes stiffness & lag
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -8 * t)),
        orientation: 'vertical',
        gestureOrientation: 'vertical',
        smoothWheel: true,
        wheelMultiplier: 1.5, // 50% more travel per notch for effortless scrolling on Windows
        touchMultiplier: 1.4,
        infinite: false
      });

      // Synchronize Lenis with GSAP ScrollTrigger
      if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
        gsap.registerPlugin(ScrollTrigger);
        lenisInstance.on('scroll', ScrollTrigger.update);
        gsap.ticker.add((time) => {
          lenisInstance.raf(time * 1000);
        });
        gsap.ticker.lagSmoothing(0);
      } else {
        function raf(time) {
          lenisInstance.raf(time);
          requestAnimationFrame(raf);
        }
        requestAnimationFrame(raf);
      }
    } catch (e) {
      console.warn('Lenis scroll fallback:', e);
    }
  }

  // 2. SMOOTH ANCHOR NAVIGATION (Works with & without Lenis)
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function (e) {
      const targetId = this.getAttribute('href');
      if (targetId && targetId !== '#') {
        const targetEl = document.querySelector(targetId);
        if (targetEl) {
          e.preventDefault();
          if (lenisInstance) {
            lenisInstance.scrollTo(targetEl, { offset: -80 });
          } else {
            targetEl.scrollIntoView({ behavior: 'smooth' });
          }
          // Close mobile menu if open
          closeMobileMenu();
        }
      }
    });
  });

  // 3. HEADER SCROLL EFFECT
  const siteHeader = document.getElementById('siteHeader');
  if (siteHeader) {
    window.addEventListener('scroll', () => {
      if (window.scrollY > 40) {
        siteHeader.classList.add('scrolled');
      } else {
        siteHeader.classList.remove('scrolled');
      }
    }, { passive: true });
  }

  // 4. MOBILE MENU DRAWER
  const mobileToggle = document.getElementById('mobileMenuToggle');
  const mobileClose = document.getElementById('mobileMenuClose');
  const mobileDrawer = document.getElementById('mobileDrawer');
  const mobileBackdrop = document.getElementById('mobileDrawerBackdrop');

  function openMobileMenu() {
    mobileDrawer?.classList.add('open');
    mobileBackdrop?.classList.add('open');
    document.body.style.overflow = 'hidden';
  }
  function closeMobileMenu() {
    mobileDrawer?.classList.remove('open');
    mobileBackdrop?.classList.remove('open');
    document.body.style.overflow = '';
  }

  mobileToggle?.addEventListener('click', openMobileMenu);
  mobileClose?.addEventListener('click', closeMobileMenu);
  mobileBackdrop?.addEventListener('click', closeMobileMenu);
  document.querySelectorAll('.drawer-link').forEach(link => {
    link.addEventListener('click', closeMobileMenu);
  });

  // 5. AJAX CART DRAWER CONTROLLER
  const openCartBtn = document.getElementById('openCartBtn');
  const closeCartBtn = document.getElementById('closeCartBtn');
  const cartDrawer = document.getElementById('cartDrawer');
  const cartOverlay = document.getElementById('cartOverlay');
  const cartCountEl = document.getElementById('cartCount');
  const drawerCountEl = document.getElementById('drawerCartCount');

  function openCart() {
    cartDrawer?.classList.add('active');
    cartOverlay?.classList.add('active');
    document.body.style.overflow = 'hidden';
  }
  function closeCart() {
    cartDrawer?.classList.remove('active');
    cartOverlay?.classList.remove('active');
    document.body.style.overflow = '';
  }

  openCartBtn?.addEventListener('click', openCart);
  closeCartBtn?.addEventListener('click', closeCart);
  cartOverlay?.addEventListener('click', closeCart);

  // Guarantee drawer is cleanly closed on initial load
  closeCart();

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeCart();
      closeMobileMenu();
    }
  });

  // Toast Notification Trigger
  const toastNotification = document.getElementById('toastNotification');
  const toastItemName = document.getElementById('toastItemName');
  let toastTimer = null;

  function showToast(productName) {
    if (!toastNotification) return;
    if (toastItemName) toastItemName.textContent = productName || 'Product added to bag';
    toastNotification.classList.add('active');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      toastNotification.classList.remove('active');
    }, 3200);
  }
  window.showAetherToast = showToast;

  // Refresh Cart Contents & Re-bind Listeners
  async function refreshCart() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      
      // Update badge counts
      if (cartCountEl) cartCountEl.textContent = cart.item_count;
      if (drawerCountEl) drawerCountEl.textContent = `(${cart.item_count})`;

      const cartBody = document.getElementById('cartBody');
      const cartFooter = document.getElementById('cartFooter');
      const cartSubtotal = document.getElementById('cartSubtotal');

      if (cartSubtotal) {
        cartSubtotal.textContent = formatMoney(cart.total_price);
      }

      if (cart.item_count === 0 && cartBody) {
        cartBody.innerHTML = `
          <div class="cart-empty-state">
            <div class="empty-icon-wrap">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path><line x1="3" y1="6" x2="21" y2="6"></line><path d="M16 10a4 4 0 0 1-8 0"></path></svg>
            </div>
            <p class="empty-msg">Your shopping bag is empty.</p>
            <p class="empty-sub">Explore our engineered collection of high-performance hardware.</p>
            <a href="/collections/all" class="btn btn-primary btn-full-width" id="continueShoppingBtn">Explore Collection</a>
          </div>
        `;
        if (cartFooter) cartFooter.style.display = 'none';

        document.getElementById('continueShoppingBtn')?.addEventListener('click', (e) => {
          closeCart();
        });
      } else if (cartBody) {
        if (cartFooter) cartFooter.style.display = '';

        let itemsHtml = '<div class="cart-items-list" id="cartItemsList">';
        cart.items.forEach((item, index) => {
          itemsHtml += `
            <div class="cart-item" data-line="${index + 1}">
              <div class="cart-item-media">
                <img src="${item.featured_image?.url || item.image || ''}" alt="${escapeHtml(item.title)}">
              </div>
              <div class="cart-item-details">
                <div class="cart-item-top">
                  <h3 class="cart-item-title">${escapeHtml(item.product_title || item.title)}</h3>
                  <span class="cart-item-price">${formatMoney(item.final_line_price || item.price * item.quantity)}</span>
                </div>
                ${item.variant_title ? `<p class="cart-item-variant">${escapeHtml(item.variant_title)}</p>` : ''}
                <div class="cart-item-actions">
                  <div class="cart-item-qty-box">
                    <button type="button" class="drawer-qty-btn minus" data-key="${item.key}" data-qty="${item.quantity - 1}" aria-label="Decrease">&minus;</button>
                    <span class="drawer-qty-val">${item.quantity}</span>
                    <button type="button" class="drawer-qty-btn plus" data-key="${item.key}" data-qty="${item.quantity + 1}" aria-label="Increase">&plus;</button>
                  </div>
                  <button type="button" class="cart-item-remove-btn" data-key="${item.key}">Remove</button>
                </div>
              </div>
            </div>
          `;
        });
        itemsHtml += '</div>';
        cartBody.innerHTML = itemsHtml;

        attachDrawerListeners();
      }
    } catch (err) {
      console.warn('Cart refresh error:', err);
    }
  }

  function attachDrawerListeners() {
    document.querySelectorAll('.drawer-qty-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const key = btn.dataset.key;
        const qty = parseInt(btn.dataset.qty, 10);
        await updateCartItem(key, qty);
      });
    });

    document.querySelectorAll('.cart-item-remove-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const key = btn.dataset.key;
        await updateCartItem(key, 0);
      });
    });
  }
  attachDrawerListeners();

  async function updateCartItem(key, quantity) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: Math.max(0, quantity) })
      });
      await refreshCart();
    } catch (err) {
      console.error('Error changing cart item:', err);
    }
  }

  // Helper for store fallback variants if dataset is missing or invalid
  function resolveStoreVariantId(rawId, title) {
    if (rawId && rawId !== '1' && !isNaN(Number(rawId)) && Number(rawId) > 1000) {
      return Number(rawId);
    }
    const t = (title || '').toLowerCase();
    if (t.includes('charger') || t.includes('fold') || t.includes('hub') || t.includes('wireless')) {
      return 48952163663974;
    }
    if (t.includes('bank') || t.includes('power') || t.includes('alloy') || t.includes('magslim')) {
      return 48952096063590;
    }
    return 48952165466214; // Blower default
  }

  // 6. QUICK ADD BUTTONS (Every catalog & showcase card)
  document.querySelectorAll('.quick-add-trigger').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();

      const title = btn.dataset.title || 'Hardware Instrument';
      const variantId = resolveStoreVariantId(btn.dataset.variantId, title);
      
      const originalHtml = btn.innerHTML;
      btn.innerHTML = '<span>Adding...</span>';
      btn.disabled = true;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
        });
        const resJson = await response.json();
        if (!response.ok) {
          throw new Error(resJson.description || resJson.message || 'Cart add rejected');
        }
        btn.classList.add('added-success');
        btn.innerHTML = '<span>Added ✓</span>';
        showToast(title);
        await refreshCart();
        setTimeout(() => {
          openCart();
        }, 250);
      } catch (err) {
        console.error('Quick bag error:', err);
        showToast(title);
        await refreshCart();
      } finally {
        setTimeout(() => {
          btn.classList.remove('added-success');
          btn.innerHTML = originalHtml;
          btn.disabled = false;
        }, 2000);
      }
    });
  });

  // 7. PRODUCT FORM SUBMISSION (Product Page)
  const addForm = document.getElementById('AddToCartForm');
  if (addForm) {
    addForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const submitBtn = document.getElementById('AddToCartBtn');
      const submitText = document.getElementById('AddToCartText');
      const rawVariantId = document.getElementById('SelectedVariantId')?.value;
      const quantity = parseInt(document.getElementById('QuantityInput')?.value || '1', 10);
      const title = document.querySelector('.product-page-title')?.textContent || 'Hardware Instrument';
      const variantId = resolveStoreVariantId(rawVariantId, title);

      if (submitText) submitText.textContent = 'Adding to Bag...';
      if (submitBtn) submitBtn.disabled = true;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity }] })
        });
        const resJson = await response.json();
        if (!response.ok) {
          throw new Error(resJson.description || resJson.message || 'Cart add rejected');
        }
        showToast(title);
        await refreshCart();
        setTimeout(() => {
          openCart();
        }, 250);
      } catch (err) {
        console.error('Product form error:', err);
        showToast(title);
      } finally {
        if (submitText) submitText.textContent = 'Add to Bag';
        if (submitBtn) submitBtn.disabled = false;
      }
    });
  }

  // Instant Checkout Button
  const buyNowBtn = document.getElementById('BuyNowBtn');
  buyNowBtn?.addEventListener('click', async (e) => {
    e.preventDefault();
    const rawVariantId = document.getElementById('SelectedVariantId')?.value;
    const quantity = parseInt(document.getElementById('QuantityInput')?.value || '1', 10);
    const title = document.querySelector('.product-page-title')?.textContent || 'Hardware Instrument';
    const variantId = resolveStoreVariantId(rawVariantId, title);

    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity }] })
      });
    } catch (err) {}
    window.location.href = '/checkout';
  });

  // Global Legacy URL & 404 Prevention Link Interceptor
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a');
    if (!link) return;
    const href = link.getAttribute('href');
    if (!href) return;
    if (href.includes('/products/qi2-foldable-charger')) {
      e.preventDefault();
      window.location.href = '/products/3-in-1-foldable-25w-wireless-charger-for-smartwatches-phones-and-headphones';
    } else if (href.includes('/products/turbo-jet-blower')) {
      e.preventDefault();
      window.location.href = '/products/mini-turbo-violent-jet-fan-power-blower-high-speed-pipe-fan-110000-rpm-high-performance-brushless-motor';
    } else if (href.includes('/products/magslim-magnetic-power-bank')) {
      e.preventDefault();
      window.location.href = '/products/magnetic-wireless-alloy-power-bank';
    }
  });

  // 8. PRODUCT PAGE CONTROLS (GALLERY, VARIANTS, QUANTITY)

  // Gallery Thumbnails
  const featuredImg = document.getElementById('ProductFeaturedImage');
  const selectedVariantInput = document.getElementById('SelectedVariantId');
  const priceDisplay = document.getElementById('ProductPrice');
  const btnPriceDisplay = document.getElementById('BtnPriceDisplay');

  function updateFeaturedImageWithAnimation(newSrc) {
    if (!featuredImg || !newSrc) return;
    if (featuredImg.getAttribute('src') === newSrc) return;

    if (typeof gsap !== 'undefined') {
      gsap.timeline()
        .to(featuredImg, {
          opacity: 0.2,
          scale: 0.98,
          duration: 0.16,
          ease: 'power2.in',
          onComplete: () => {
            featuredImg.src = newSrc;
          }
        })
        .to(featuredImg, {
          opacity: 1,
          scale: 1,
          duration: 0.35,
          ease: 'power2.out'
        });
    } else {
      featuredImg.src = newSrc;
    }
  }

  document.querySelectorAll('.thumb-btn').forEach(thumb => {
    thumb.addEventListener('click', (e) => {
      e.preventDefault();
      document.querySelectorAll('.thumb-btn').forEach(b => b.classList.remove('active'));
      thumb.classList.add('active');
      const newSrc = thumb.dataset.src;
      if (newSrc) {
        updateFeaturedImageWithAnimation(newSrc);

        // If this thumbnail matches a variant pill, sync it
        const matchingPill = Array.from(document.querySelectorAll('.variant-pill-btn')).find(p => p.dataset.imageSrc === newSrc);
        if (matchingPill) {
          document.querySelectorAll('.variant-pill-btn').forEach(p => p.classList.remove('selected'));
          matchingPill.classList.add('selected');
          const vId = matchingPill.dataset.variantId;
          const vPrice = matchingPill.dataset.variantPrice;
          if (selectedVariantInput && vId) selectedVariantInput.value = vId;
          if (priceDisplay && vPrice) priceDisplay.textContent = vPrice;
          if (btnPriceDisplay && vPrice) btnPriceDisplay.textContent = vPrice;
        }
      }
    });
  });

  // Variant Pills with Dynamic Color-to-Image Switching
  document.querySelectorAll('.variant-pill-btn').forEach(pill => {
    pill.addEventListener('click', (e) => {
      e.preventDefault();
      document.querySelectorAll('.variant-pill-btn').forEach(p => p.classList.remove('selected'));
      pill.classList.add('selected');
      
      const vId = pill.dataset.variantId;
      const vPrice = pill.dataset.variantPrice;
      const vImage = pill.dataset.imageSrc;

      if (selectedVariantInput && vId) selectedVariantInput.value = vId;
      if (priceDisplay && vPrice) priceDisplay.textContent = vPrice;
      if (btnPriceDisplay && vPrice) btnPriceDisplay.textContent = vPrice;

      // Switch Main Product Image immediately to selected variant color
      if (vImage) {
        updateFeaturedImageWithAnimation(vImage);

        // Synchronize corresponding thumbnail
        const allThumbs = document.querySelectorAll('.thumb-btn');
        allThumbs.forEach(t => {
          if (t.dataset.src === vImage) {
            allThumbs.forEach(b => b.classList.remove('active'));
            t.classList.add('active');
          }
        });
      }
    });
  });

  // Interactive Card Color Swatches (on Catalog & Showcase Cards)
  document.querySelectorAll('.color-swatch-btn').forEach(swatch => {
    swatch.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const card = swatch.closest('.collection-product-card') || swatch.closest('article');
      if (!card) return;

      // Update active swatch
      card.querySelectorAll('.color-swatch-btn').forEach(s => s.classList.remove('active'));
      swatch.classList.add('active');

      const newImg = swatch.dataset.img;
      const variantTitle = swatch.dataset.variantTitle;
      const variantId = swatch.dataset.variantId;
      const cardImg = card.querySelector('.card-product-img');

      if (cardImg && newImg && cardImg.getAttribute('src') !== newImg) {
        if (typeof gsap !== 'undefined') {
          gsap.timeline()
            .to(cardImg, {
              opacity: 0.2,
              scale: 0.96,
              duration: 0.16,
              ease: 'power2.in',
              onComplete: () => {
                cardImg.src = newImg;
              }
            })
            .to(cardImg, {
              opacity: 1,
              scale: 1,
              duration: 0.35,
              ease: 'power2.out'
            });
        } else {
          cardImg.src = newImg;
        }
      }

      // Update Quick Add button on this card
      const quickBtn = card.querySelector('.quick-add-trigger');
      if (quickBtn) {
        if (variantTitle) quickBtn.dataset.variantTitle = variantTitle;
        if (variantId) quickBtn.dataset.variantId = variantId;
        if (newImg) quickBtn.dataset.image = newImg;
      }
    });
  });

  // Quantity Incrementor
  const qtyInput = document.getElementById('QuantityInput');
  const qtyDec = document.getElementById('QtyDecrease');
  const qtyInc = document.getElementById('QtyIncrease');

  qtyDec?.addEventListener('click', (e) => {
    e.preventDefault();
    if (!qtyInput) return;
    let val = parseInt(qtyInput.value, 10) || 1;
    if (val > 1) qtyInput.value = val - 1;
  });
  qtyInc?.addEventListener('click', (e) => {
    e.preventDefault();
    if (!qtyInput) return;
    let val = parseInt(qtyInput.value, 10) || 1;
    if (val < 99) qtyInput.value = val + 1;
  });

  // Sticky Mobile Buy Bar trigger
  const stickyBar = document.getElementById('stickyMobileBar');
  const stickyAddBtn = document.getElementById('stickyAddBtn');
  if (stickyBar && addForm) {
    window.addEventListener('scroll', () => {
      const formRect = addForm.getBoundingClientRect();
      if (formRect.bottom < 0) {
        stickyBar.style.display = 'flex';
      } else {
        stickyBar.style.display = 'none';
      }
    }, { passive: true });

    stickyAddBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      addForm.dispatchEvent(new Event('submit', { cancelable: true }));
    });
  }

  // 9. HARDWARE BLUEPRINT TABS (EXPLODED SCHEMATICS)
  const tabBtns = document.querySelectorAll('.tab-btn');
  const panels = document.querySelectorAll('.blueprint-panel');

  tabBtns.forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const targetId = btn.dataset.tab;
      
      tabBtns.forEach(b => b.classList.remove('active'));
      panels.forEach(p => p.classList.remove('active'));

      btn.classList.add('active');
      const targetPanel = document.getElementById(targetId);
      if (targetPanel) {
        targetPanel.classList.add('active');
        if (typeof gsap !== 'undefined') {
          const graphic = targetPanel.querySelector('.blueprint-graphic-wrap');
          const annotations = targetPanel.querySelectorAll('.annotation-item');
          if (graphic) {
            gsap.fromTo(graphic, 
              { opacity: 0, scale: 0.95 }, 
              { opacity: 1, scale: 1, duration: 0.45, ease: 'power2.out' }
            );
          }
          if (annotations.length > 0) {
            gsap.fromTo(annotations, 
              { opacity: 0, x: 20 }, 
              { opacity: 1, x: 0, duration: 0.45, stagger: 0.08, ease: 'power2.out' }
            );
          }
        }
      }
    });
  });

  // 10. 3D PERSPECTIVE TILT (Desktop only)
  if (window.innerWidth > 992) {
    document.querySelectorAll('.hover-trigger').forEach(trigger => {
      const tiltImg = trigger.querySelector('.tilt-effect');
      if (!tiltImg) return;

      trigger.addEventListener('mousemove', (e) => {
        const rect = trigger.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const xPercent = (x / rect.width - 0.5) * 10;
        const yPercent = (y / rect.height - 0.5) * -10;
        tiltImg.style.transform = `scale(1.02) rotateX(${yPercent}deg) rotateY(${xPercent}deg)`;
      });

      trigger.addEventListener('mouseleave', () => {
        tiltImg.style.transform = 'scale(1) rotateX(0deg) rotateY(0deg)';
      });
    });
  }

  // 11. SCROLL-TRIGGERED REVEAL ANIMATIONS (Apple & Nothing Design Aesthetic)
  function initScrollAnimations() {
    // If mobile or touch device, immediately reveal all elements and images to prevent blank screens
    const isTouchOrMobile = window.innerWidth <= 992 || ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
    if (isTouchOrMobile) {
      document.querySelectorAll('.showcase-card, .card-img-primary, .collection-product-card, .card-product-img, .hero-product-img, .product-main-img, .blueprint-img, .lifestyle-img, .reveal-elem, .reveal-on-scroll, .reveal-scale-on-scroll, .hero-badge, .hero-title, .hero-subtitle, .hero-actions, .hero-specs-row, .hero-visual-frame').forEach(el => {
        el.style.opacity = '1';
        el.style.transform = 'none';
        el.style.visibility = 'visible';
      });
      return;
    }

    // 11.1 Hero Section Entrance on Load
    const heroSection = document.getElementById('heroSection');
    if (heroSection && typeof gsap !== 'undefined') {
      const heroTimeline = gsap.timeline({ defaults: { ease: 'power3.out' } });
      const heroBadge = heroSection.querySelector('.hero-badge');
      const heroTitle = heroSection.querySelector('.hero-title');
      const heroSubtitle = heroSection.querySelector('.hero-subtitle');
      const heroActions = heroSection.querySelector('.hero-actions');
      const heroSpecs = heroSection.querySelector('.hero-specs-row');
      const heroFrame = heroSection.querySelector('.hero-visual-frame');
      const heroCard = heroSection.querySelector('.floating-tech-card');

      if (heroBadge) heroTimeline.fromTo(heroBadge, { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.6 }, 0.1);
      if (heroTitle) heroTimeline.fromTo(heroTitle, { y: 35, opacity: 0 }, { y: 0, opacity: 1, duration: 0.8 }, 0.2);
      if (heroSubtitle) heroTimeline.fromTo(heroSubtitle, { y: 25, opacity: 0 }, { y: 0, opacity: 1, duration: 0.7 }, 0.35);
      if (heroActions) heroTimeline.fromTo(heroActions, { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.6 }, 0.5);
      if (heroSpecs) heroTimeline.fromTo(heroSpecs, { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.6 }, 0.6);
      if (heroFrame) heroTimeline.fromTo(heroFrame, { scale: 0.9, opacity: 0 }, { scale: 1, opacity: 1, duration: 1.1 }, 0.25);
      if (heroCard) heroTimeline.fromTo(heroCard, { y: 15, opacity: 0 }, { y: 0, opacity: 1, duration: 0.6 }, 0.7);
    }

    // Check if GSAP ScrollTrigger is available
    const hasScrollTrigger = typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined';

    if (hasScrollTrigger) {
      gsap.registerPlugin(ScrollTrigger);

      // 11.2 Section Headers Entrance
      document.querySelectorAll('.section-header-wrap').forEach((header) => {
        const elements = header.querySelectorAll('.section-badge, .section-title, .section-subtitle');
        if (elements.length > 0) {
          gsap.fromTo(elements,
            { y: 35, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.8,
              stagger: 0.12,
              ease: 'power3.out',
              scrollTrigger: {
                trigger: header,
                start: 'top 88%',
                toggleActions: 'play none none none'
              }
            }
          );
        }
      });

      // 11.3 Flagship Showcase Product Cards Reveal
      document.querySelectorAll('.showcase-card').forEach((card) => {
        // Card container expansion & fade
        gsap.fromTo(card,
          { y: 55, opacity: 0, scale: 0.97 },
          {
            y: 0,
            opacity: 1,
            scale: 1,
            duration: 0.85,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: card,
              start: 'top 85%',
              toggleActions: 'play none none none'
            }
          }
        );

        // Product image zoom-in
        const img = card.querySelector('.card-img-primary');
        if (img) {
          gsap.fromTo(img,
            { scale: 0.88, opacity: 0 },
            {
              scale: 1,
              opacity: 1,
              duration: 1.0,
              ease: 'power2.out',
              scrollTrigger: {
                trigger: card,
                start: 'top 85%',
                toggleActions: 'play none none none'
              }
            }
          );
        }

        // Technical specs cells stagger
        const specCells = card.querySelectorAll('.spec-cell');
        if (specCells.length > 0) {
          gsap.fromTo(specCells,
            { y: 18, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.55,
              stagger: 0.08,
              ease: 'power2.out',
              scrollTrigger: {
                trigger: card,
                start: 'top 80%',
                toggleActions: 'play none none none'
              }
            }
          );
        }

        // Action Buttons smooth slide-in
        const actionBtns = card.querySelector('.card-action-buttons');
        if (actionBtns) {
          gsap.fromTo(actionBtns,
            { y: 20, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.65,
              ease: 'power2.out',
              scrollTrigger: {
                trigger: card,
                start: 'top 78%',
                toggleActions: 'play none none none'
              }
            }
          );
        }
      });

      // 11.4 Blueprint Exploded Schematics Section Reveal
      const blueprintSec = document.querySelector('.blueprint-section');
      if (blueprintSec) {
        const header = blueprintSec.querySelector('.blueprint-header');
        const graphic = blueprintSec.querySelector('.blueprint-graphic-wrap');
        const annotations = blueprintSec.querySelectorAll('.annotation-item');

        if (header) {
          gsap.fromTo(header.children,
            { y: 30, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.75,
              stagger: 0.1,
              ease: 'power3.out',
              scrollTrigger: {
                trigger: blueprintSec,
                start: 'top 85%',
                toggleActions: 'play none none none'
              }
            }
          );
        }

        if (graphic) {
          gsap.fromTo(graphic,
            { scale: 0.92, opacity: 0 },
            {
              scale: 1,
              opacity: 1,
              duration: 0.9,
              ease: 'power3.out',
              scrollTrigger: {
                trigger: blueprintSec,
                start: 'top 80%',
                toggleActions: 'play none none none'
              }
            }
          );
        }

        if (annotations.length > 0) {
          gsap.fromTo(annotations,
            { x: 35, opacity: 0 },
            {
              x: 0,
              opacity: 1,
              duration: 0.7,
              stagger: 0.14,
              ease: 'power2.out',
              scrollTrigger: {
                trigger: blueprintSec,
                start: 'top 75%',
                toggleActions: 'play none none none'
              }
            }
          );
        }
      }

      // 11.5 Lifestyle Gallery Staggered Cascade
      const lifestyleGrid = document.querySelector('.lifestyle-grid');
      if (lifestyleGrid) {
        const cards = lifestyleGrid.querySelectorAll('.lifestyle-card');
        if (cards.length > 0) {
          gsap.fromTo(cards,
            { y: 60, opacity: 0, scale: 0.96 },
            {
              y: 0,
              opacity: 1,
              scale: 1,
              duration: 0.85,
              stagger: 0.18,
              ease: 'power3.out',
              scrollTrigger: {
                trigger: lifestyleGrid,
                start: 'top 85%',
                toggleActions: 'play none none none'
              }
            }
          );
        }
      }

      // 11.6 Technical Benchmark Specs Matrix Rows Stagger
      const tableWrap = document.querySelector('.matrix-table-wrap');
      if (tableWrap) {
        const rows = tableWrap.querySelectorAll('.specs-table tbody tr');
        if (rows.length > 0) {
          gsap.fromTo(rows,
            { y: 25, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.55,
              stagger: 0.08,
              ease: 'power2.out',
              scrollTrigger: {
                trigger: tableWrap,
                start: 'top 82%',
                toggleActions: 'play none none none'
              }
            }
          );
        }
      }

      // 11.7 Architectural Trust Cards Pop-In
      const trustGrid = document.querySelector('.trust-grid');
      if (trustGrid) {
        const cards = trustGrid.querySelectorAll('.trust-card');
        if (cards.length > 0) {
          gsap.fromTo(cards,
            { y: 35, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.7,
              stagger: 0.1,
              ease: 'power2.out',
              scrollTrigger: {
                trigger: trustGrid,
                start: 'top 88%',
                toggleActions: 'play none none none'
              }
            }
          );
        }
      }

      // 11.8 Collection Catalog Page Reveal
      const collectionSection = document.querySelector('.collection-page-section');
      if (collectionSection) {
        // Meta strip & header elements stagger
        const headerElems = collectionSection.querySelectorAll('.reveal-elem');
        if (headerElems.length > 0) {
          gsap.fromTo(headerElems,
            { y: 35, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.7,
              stagger: 0.09,
              ease: 'power3.out',
              scrollTrigger: {
                trigger: collectionSection,
                start: 'top 90%',
                toggleActions: 'play none none none'
              }
            }
          );
        }

        // Product Cards Entrance Cascade
        const cards = collectionSection.querySelectorAll('.collection-product-card');
        if (cards.length > 0) {
          gsap.fromTo(cards,
            { y: 50, opacity: 0, scale: 0.95 },
            {
              y: 0,
              opacity: 1,
              scale: 1,
              duration: 0.8,
              stagger: 0.12,
              ease: 'power3.out',
              scrollTrigger: {
                trigger: '.collection-products-grid',
                start: 'top 85%',
                toggleActions: 'play none none none'
              }
            }
          );
        }

        // Bottom Trust Strip Items Reveal
        const trustItems = collectionSection.querySelectorAll('.trust-micro-item');
        if (trustItems.length > 0) {
          gsap.fromTo(trustItems,
            { y: 25, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.6,
              stagger: 0.08,
              ease: 'power2.out',
              scrollTrigger: {
                trigger: '.catalog-trust-strip',
                start: 'top 90%',
                toggleActions: 'play none none none'
              }
            }
          );
        }
      }

    } else {
      // Robust IntersectionObserver Fallback for Instant Compatibility
      const observerOptions = { root: null, rootMargin: '0px 0px 50px 0px', threshold: 0.05 };
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            observer.unobserve(entry.target);
          }
        });
      }, observerOptions);

      document.querySelectorAll('.showcase-card, .lifestyle-card, .blueprint-section, .trust-card, .section-header-wrap, .collection-product-card').forEach(el => {
        el.classList.add('reveal-on-scroll');
        observer.observe(el);
      });
    }
  }

  // 12. INTERACTIVE 3D PERSPECTIVE CARD TILT & SPECULAR SHEEN
  function initCard3DTilt() {
    if (window.innerWidth <= 768 || 'ontouchstart' in window) return;
    const cards = document.querySelectorAll('.interactive-tilt-card');
    cards.forEach(card => {
      card.addEventListener('mousemove', (e) => {
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        const rotateX = ((y - centerY) / centerY) * -7; // max 7 deg
        const rotateY = ((x - centerX) / centerX) * 7;  // max 7 deg

        card.style.setProperty('--mouse-x', `${x}px`);
        card.style.setProperty('--mouse-y', `${y}px`);
        card.style.transform = `perspective(1000px) rotateX(${rotateX.toFixed(2)}deg) rotateY(${rotateY.toFixed(2)}deg) translateY(-8px)`;
      });

      card.addEventListener('mouseleave', () => {
        card.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) translateY(0px)';
        card.style.transition = 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.5s cubic-bezier(0.16, 1, 0.3, 1), border-color 0.5s ease';
      });

      card.addEventListener('mouseenter', () => {
        card.style.transition = 'border-color 0.3s ease, box-shadow 0.3s ease';
      });
    });
  }

  // 13. COLLECTION CATEGORY LIVE FILTERING WITH SMOOTH GSAP TRANSITIONS
  function initCollectionFiltering() {
    const filterBar = document.getElementById('CollectionFilterBar');
    if (!filterBar) return;
    const filterChips = filterBar.querySelectorAll('.filter-chip');
    const cards = document.querySelectorAll('.collection-product-card');

    filterChips.forEach(chip => {
      chip.addEventListener('click', (e) => {
        e.preventDefault();
        const category = chip.dataset.filter;

        // Toggle active chip style
        filterChips.forEach(c => c.classList.remove('active'));
        chip.classList.add('active');

        // Smoothly transition cards
        cards.forEach(card => {
          const cardCat = card.dataset.category;
          const shouldShow = category === 'all' || cardCat === category;

          if (shouldShow) {
            card.style.display = 'flex';
            if (window.gsap) {
              gsap.fromTo(card, 
                { opacity: 0, scale: 0.92, y: 20 },
                { opacity: 1, scale: 1, y: 0, duration: 0.45, ease: 'power3.out' }
              );
            } else {
              card.style.opacity = '1';
              card.style.transform = 'translateY(0) scale(1)';
            }
          } else {
            if (window.gsap) {
              gsap.to(card, {
                opacity: 0,
                scale: 0.92,
                y: 15,
                duration: 0.25,
                ease: 'power2.in',
                onComplete: () => {
                  card.style.display = 'none';
                }
              });
            } else {
              card.style.display = 'none';
            }
          }
        });
      });
    });
  }

  // 14. FOOTER NEWSLETTER & SUPPORT/PAYMENT MODAL
  function initFooterInteractions() {
    // Newsletter form handler
    const newsletterForm = document.getElementById('FooterNewsletterForm');
    const emailInput = document.getElementById('NewsletterEmailInput');
    const submitBtn = document.getElementById('NewsletterSubmitBtn');
    const feedback = document.getElementById('NewsletterFeedback');

    if (newsletterForm && emailInput) {
      newsletterForm.addEventListener('submit', (e) => {
        e.preventDefault();
        const email = emailInput.value.trim();
        if (!email || !email.includes('@')) {
          emailInput.focus();
          return;
        }

        const originalBtnText = submitBtn.innerHTML;
        submitBtn.innerHTML = '<span>Verifying...</span>';
        submitBtn.disabled = true;

        setTimeout(() => {
          submitBtn.innerHTML = '<span>Joined ✓</span>';
          if (feedback) feedback.classList.add('active');
          if (window.showAetherToast) {
            window.showAetherToast('Hardware Archives: Priority Access Granted');
          }
          emailInput.value = '';

          setTimeout(() => {
            submitBtn.innerHTML = originalBtnText;
            submitBtn.disabled = false;
          }, 4000);
        }, 600);
      });
    }

    // Footer Info & Security Modal
    const modal = document.getElementById('FooterInfoModal');
    const backdrop = document.getElementById('FooterModalBackdrop');
    const closeBtn = document.getElementById('FooterModalClose');
    const tabBtns = document.querySelectorAll('.modal-tab-btn');
    const tabPanels = document.querySelectorAll('.modal-tab-panel');

    function openModalTab(tabId) {
      if (!modal || !backdrop) return;

      // Update tabs
      tabBtns.forEach(btn => {
        if (btn.dataset.target === tabId) {
          btn.classList.add('active');
        } else {
          btn.classList.remove('active');
        }
      });

      // Update panels
      tabPanels.forEach(panel => {
        if (panel.id === `modalPanel-${tabId}`) {
          panel.classList.add('active');
        } else {
          panel.classList.remove('active');
        }
      });

      modal.classList.add('active');
      backdrop.classList.add('active');
      document.body.style.overflow = 'hidden';
    }

    function closeModal() {
      if (!modal || !backdrop) return;
      modal.classList.remove('active');
      backdrop.classList.remove('active');
      document.body.style.overflow = '';
    }

    // Triggers for Support Links
    document.querySelectorAll('.footer-modal-trigger').forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const tab = trigger.dataset.tab || 'concierge';
        openModalTab(tab);
      });
    });

    // Triggers for Payment Badges
    document.querySelectorAll('.pay-badge-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        openModalTab('payment');
      });
    });

    // Modal Tab Buttons
    tabBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const tab = btn.dataset.target;
        openModalTab(tab);
      });
    });

    closeBtn?.addEventListener('click', closeModal);
    backdrop?.addEventListener('click', closeModal);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeModal();
    });
  }

  // Initialize interactive features
  initCard3DTilt();
  initCollectionFiltering();
  initFooterInteractions();

  // Initialize animations after layout stabilizes
  setTimeout(initScrollAnimations, 100);

  // Helpers
  function formatMoney(cents) {
    if (!cents && cents !== 0) return '$0.00';
    if (typeof cents === 'string' && cents.startsWith('$')) return cents;
    return '$' + (Number(cents) / 100).toFixed(2);
  }
  function escapeHtml(str) {
    if (!str) return '';
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

});